/**
 * 10.9GU — VÓRTICE DE PORTAL (shader) + FAÍSCAS. Antes o "vão" de cada portal era um disco
 * chapado pulsando. Agora é um redemoinho animado: espiral que gira pro centro, borda acesa,
 * miolo escuro (buraco) e cintilação. Aditivo, sem escrita de profundidade — cabe em qualquer
 * portal (Rift, Fenda dos Titãs, Arena PvP) trocando só a cor. Custo: 1 draw call, fragment leve.
 *
 * `uOpen` 0..1 = portal fechado/aberto (o Rift inativo fica quase apagado e parado).
 */
import * as THREE from "three";

export type VortexMaterial = THREE.ShaderMaterial & { uniforms: { uColor: { value: THREE.Color }; uTime: { value: number }; uOpen: { value: number } } };

export function makeVortexMaterial(color: number, open = 1): VortexMaterial {
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uTime: { value: 0 },
      uOpen: { value: open },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv - 0.5;
        gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
      }`,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform float uTime, uOpen;
      varying vec2 vUv;
      float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float vnoise(vec2 p) {
        vec2 i = floor(p), f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y);
      }
      void main() {
        float r = length(vUv) * 2.0;           // 0 centro … 1 borda
        if (r > 1.0) discard;
        float a = atan(vUv.y, vUv.x);
        float spd = 0.6 + 2.4 * uOpen;
        // espiral: braços que giram e se enrolam pro centro
        float arms = sin(a * 3.0 - r * 9.0 + uTime * spd) * 0.5 + 0.5;
        float arms2 = sin(a * 5.0 + r * 14.0 - uTime * spd * 1.7) * 0.5 + 0.5;
        float n = vnoise(vec2(a * 2.0 + uTime * 0.3, r * 6.0 - uTime * spd * 0.8));
        float swirl = arms * 0.55 + arms2 * 0.25 + n * 0.4;
        // miolo escuro + borda acesa
        float hole = smoothstep(0.0, 0.28, r);
        float rim = smoothstep(0.55, 0.98, r) * (1.0 - smoothstep(0.98, 1.0, r));
        float body = swirl * hole * (1.0 - smoothstep(0.82, 1.0, r));
        float v = (body * 0.9 + rim * 1.1) * (0.18 + 0.82 * uOpen);
        vec3 col = uColor * v + vec3(1.0) * rim * 0.35 * uOpen;
        gl_FragColor = vec4(col, v);
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
  return mat as VortexMaterial;
}

/** Faíscas subindo em volta do portal: nuvem de pontos aditiva (1 draw call), reciclada no topo. */
export interface PortalSparks { points: THREE.Points; update(dt: number): void; setColor(c: number): void; setOn(v: boolean): void }

export function makePortalSparks(parent: THREE.Object3D, color: number, radius = 1.6, count = 26, height = 4.2): PortalSparks {
  const pos = new Float32Array(count * 3);
  const speed = new Float32Array(count);
  const ang = new Float32Array(count);
  const rad = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    ang[i] = Math.random() * Math.PI * 2;
    rad[i] = radius * (0.5 + Math.random() * 0.6);
    speed[i] = 0.5 + Math.random() * 0.9;
    pos[i * 3] = Math.cos(ang[i]) * rad[i];
    pos[i * 3 + 1] = Math.random() * height;
    pos[i * 3 + 2] = Math.sin(ang[i]) * rad[i];
  }
  const geo = new THREE.BufferGeometry();
  const attr = new THREE.BufferAttribute(pos, 3);
  geo.setAttribute("position", attr);
  const mat = new THREE.PointsMaterial({ color, size: 0.16, transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  parent.add(points);
  let t = 0;
  let on = true;
  return {
    points,
    setColor: (c) => mat.color.setHex(c),
    setOn: (v) => { on = v; points.visible = v; },
    update: (dt) => {
      if (!on) return;
      t += dt;
      for (let i = 0; i < count; i++) {
        let y = pos[i * 3 + 1] + speed[i] * dt;
        if (y > height) { y = 0; ang[i] = Math.random() * Math.PI * 2; }
        const a = ang[i] + t * 0.5;
        const r = rad[i] * (1 - y / height * 0.45); // afunila pra cima
        pos[i * 3] = Math.cos(a) * r;
        pos[i * 3 + 1] = y;
        pos[i * 3 + 2] = Math.sin(a) * r;
      }
      attr.needsUpdate = true;
    },
  };
}
